import { Injectable } from '@angular/core';
import { ApiService } from '@core/api/api.service';
import {
  OnboardingContractCreateResult,
  OnboardingContractStatus,
} from '@core/models/subscription.model';
import { Observable, of } from 'rxjs';
import { finalize, map, shareReplay, tap } from 'rxjs/operators';

export interface OnboardingContractStatusOptions {
  /** When true, bypass the short-lived cache (use for polling / manual continue). */
  fresh?: boolean;
}

/** Raw DRF body — camelCase or snake_case depending on renderer. */
interface OnboardingContractStatusRaw {
  required?: boolean;
  isSigned?: boolean;
  is_signed?: boolean;
  isOrgSigned?: boolean;
  is_org_signed?: boolean;
  envelopeSigningEmbed?: string | null;
  envelope_signing_embed?: string | null;
}

interface OnboardingContractCreateRaw {
  required?: boolean;
  isSigned?: boolean;
  is_signed?: boolean;
  isOrgSigned?: boolean;
  is_org_signed?: boolean;
  envelopeSigningEmbed?: string | null;
  envelope_signing_embed?: string | null;
}

/**
 * Onboarding SaaS contract signing (KAP-1875).
 *
 * Separate from basket Signable (`ClientService.fetchSignDocumentData`) — those
 * endpoints are basket-item bound. Org is resolved server-side from the session.
 *
 * Gate tier flows on `isOrgSigned`, not `isSigned` alone: Kapnative may
 * countersign after the org, while tier assignment unlocks on the org signature.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingContractService {
  private readonly statusUrl = 'subscriptions/onboarding-contract/status/';
  private readonly createUrl = 'subscriptions/onboarding-contract/';
  private readonly statusCacheTtlMs = 3000;

  private inFlightStatus$: Observable<OnboardingContractStatus> | null = null;
  private inFlightIsFresh = false;
  private cachedStatus: OnboardingContractStatus | null = null;
  private cachedStatusAt = 0;

  constructor(private apiService: ApiService) {}

  /** Cheap polling endpoint; server refreshes Signable while unsigned. */
  getStatus(options?: OnboardingContractStatusOptions): Observable<OnboardingContractStatus> {
    const fresh = options?.fresh === true;

    if (!fresh && this.cachedStatus && Date.now() - this.cachedStatusAt < this.statusCacheTtlMs) {
      return of({ ...this.cachedStatus });
    }

    if (this.inFlightStatus$ && (!fresh || this.inFlightIsFresh)) {
      return this.inFlightStatus$;
    }

    this.inFlightIsFresh = fresh;
    this.inFlightStatus$ = this.apiService.get<OnboardingContractStatusRaw>(this.statusUrl).pipe(
      map((raw) => this.mapStatus(raw)),
      tap((status) => this.storeStatusCache(status)),
      shareReplay({ bufferSize: 1, refCount: true }),
      finalize(() => {
        this.inFlightStatus$ = null;
        this.inFlightIsFresh = false;
      }),
    );

    return this.inFlightStatus$;
  }

  /**
   * Idempotent create/fetch of the Signable envelope.
   * No request body — org is resolved from the session on the backend.
   */
  createEnvelope(): Observable<OnboardingContractCreateResult> {
    return this.apiService.post<OnboardingContractCreateRaw>(this.createUrl, null).pipe(
      map((raw) => this.mapCreateResult(raw)),
      tap(() => this.invalidateStatusCache()),
    );
  }

  /** Visible for unit tests. */
  invalidateStatusCache(): void {
    this.cachedStatus = null;
    this.cachedStatusAt = 0;
    this.inFlightStatus$ = null;
    this.inFlightIsFresh = false;
  }

  private storeStatusCache(status: OnboardingContractStatus): void {
    this.cachedStatus = status;
    this.cachedStatusAt = Date.now();
  }

  private mapStatus(raw: OnboardingContractStatusRaw | null | undefined): OnboardingContractStatus {
    const isSigned = this.readBoolean(raw?.isSigned, raw?.is_signed);
    const isOrgSigned = this.readBoolean(raw?.isOrgSigned, raw?.is_org_signed);
    return {
      isSigned,
      isOrgSigned,
      envelopeSigningEmbed: this.readEmbed(raw?.envelopeSigningEmbed, raw?.envelope_signing_embed),
      required: this.readRequired(raw?.required),
    };
  }

  private mapCreateResult(raw: OnboardingContractCreateRaw | null | undefined): OnboardingContractCreateResult {
    const isSigned = this.readBoolean(raw?.isSigned, raw?.is_signed);
    // POST responses may omit is_org_signed; a fully-signed body implies org signed.
    const isOrgSigned = this.readBoolean(raw?.isOrgSigned, raw?.is_org_signed) || isSigned;
    return {
      isSigned,
      isOrgSigned,
      envelopeSigningEmbed: this.readEmbed(raw?.envelopeSigningEmbed, raw?.envelope_signing_embed),
      required: this.readRequired(raw?.required),
    };
  }

  /** A contract is required unless the API says otherwise (older backends omit the field). */
  private readRequired(value: boolean | undefined): boolean {
    return value === false ? false : true;
  }

  private readBoolean(camel: boolean | undefined, snake: boolean | undefined): boolean {
    if (typeof camel === 'boolean') {
      return camel;
    }
    if (typeof snake === 'boolean') {
      return snake;
    }
    return false;
  }

  private readEmbed(
    camel: string | null | undefined,
    snake: string | null | undefined,
  ): string | null {
    if (typeof camel === 'string' && camel.length > 0) {
      return camel;
    }
    if (typeof snake === 'string' && snake.length > 0) {
      return snake;
    }
    return null;
  }
}

/**
 * POST create returned 404 because no template is seeded for this org type/partner.
 * Tier mutations are blocked server-side (KAP-1874) until templates exist and the
 * contract is signed — the FE must not treat this as "signed" or proceed.
 */
export function isOnboardingContractTemplateMissing(error: unknown): boolean {
  if (!isHttpErrorLike(error) || error.status !== 404) {
    return false;
  }
  const detail = readErrorDetail(error).toLowerCase();
  return detail.includes('template') || detail.includes('onboarding contract');
}

/** POST create returned 503 — Signable unreachable / misconfigured template. */
export function isOnboardingContractSigningUnavailable(error: unknown): boolean {
  return isHttpErrorLike(error) && error.status === 503;
}

export function isOnboardingContractNoOrganization(error: unknown): boolean {
  if (!isHttpErrorLike(error) || error.status !== 404) {
    return false;
  }
  if (isOnboardingContractTemplateMissing(error)) {
    return false;
  }
  const detail = readErrorDetail(error).toLowerCase();
  return detail.includes('no organization') || detail.length === 0;
}

export function isOnboardingContractNotSigned(error: unknown): boolean {
  if (!isHttpErrorLike(error) || error.status !== 403) {
    return false;
  }
  const body = error.error;
  if (body && typeof body === 'object' && (body as { code?: unknown }).code === 'contract_not_signed') {
    return true;
  }
  const detail = readErrorDetail(error).toLowerCase();
  return detail.includes('contract_not_signed') || detail.includes('contract not signed');
}

/**
 * True when the global HTTP interceptor should not snackbar — the subscription
 * upgrade dialog handles onboarding-contract and tier-gate errors itself.
 */
export function isOnboardingContractDialogHandledHttpError(error: unknown): boolean {
  if (!isHttpErrorLike(error)) {
    return false;
  }

  const url = readErrorUrl(error);

  if (isOnboardingContractApiUrl(url)) {
    return true;
  }

  return isOnboardingContractNotSigned(error) && isSubscriptionTierMutationUrl(url);
}

function isOnboardingContractApiUrl(url: string): boolean {
  const path = url.split('?')[0];
  return (
    path.includes('/subscriptions/onboarding-contract/status') ||
    path.endsWith('/subscriptions/onboarding-contract/') ||
    path.endsWith('/subscriptions/onboarding-contract')
  );
}

function isSubscriptionTierMutationUrl(url: string): boolean {
  const path = url.split('?')[0];
  return (
    path.includes('/subscriptions/billing/lp/subscribe') ||
    path.includes('/subscriptions/billing/lp/upgrade') ||
    path.includes('/common/subscription/upgrade') ||
    path.includes('/common/subscription/change/confirm') ||
    path.includes('/common/subscription/change/schedule') ||
    path.includes('/common/subscription/change/cancel')
  );
}

function readErrorUrl(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'url' in error) {
    const url = (error as { url?: unknown }).url;
    return typeof url === 'string' ? url : '';
  }
  return '';
}

function isHttpErrorLike(error: unknown): error is { status?: number; error?: unknown } {
  return typeof error === 'object' && error !== null;
}

function readErrorDetail(error: { error?: unknown }): string {
  const body = error.error;
  if (typeof body === 'string') {
    return body;
  }
  if (body && typeof body === 'object') {
    const detail = (body as { detail?: unknown }).detail;
    if (typeof detail === 'string') {
      return detail;
    }
  }
  return '';
}
